import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Divider,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Grid,
  IconButton,
  InputAdornment
} from '@mui/material';
import SEO from '../components/SEO';
import { GitHub as GitHubIcon, Visibility, VisibilityOff, MailOutline, LockOutlined, CheckCircle, Login as LoginIcon, PersonAddAlt } from '@mui/icons-material';
import { FaMicrosoft } from 'react-icons/fa';
import axios from 'axios';
import { auth } from '../firebase';
import { signInWithEmailAndPassword, GithubAuthProvider, OAuthProvider, signInWithPopup, sendPasswordResetEmail, sendEmailVerification } from 'firebase/auth';
import { setStoredUser } from '../utils/userStorage';
import API_BASE from '../apiBase';
import logger from '../services/logger';
import {
  Architecture as ArchitectureIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Science as ScienceIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { trackButtonClick, trackFormSubmit } from '../utils/gtm';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(null); // 'github', 'email', 'microsoft', 'forgot'
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState('');
  const [resendInfo, setResendInfo] = useState('');
  const [notice, setNotice] = useState(
    location.state?.verificationNotice
      ? `Verification email sent to ${location.state.verificationEmail || 'your email'}. Please verify (check spam/junk) before signing in.`
      : ''
  );
  const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const isEmailValid = EMAIL_REGEX.test(email);

  // Clear email and password on mount/refresh
  useEffect(() => {
    setEmail('');
    setPassword('');

    // Check for stored error message from previous login attempt
    const storedError = localStorage.getItem('loginError');
    if (storedError) {
      setError(storedError);
      // Clear the stored error after displaying it
      localStorage.removeItem('loginError');
    }
  }, []);



  const bootstrapAndFetchUser = async () => {
    await axios.post(`${API_BASE}/api/users/bootstrap`);
    const { data } = await axios.get(`${API_BASE}/api/users/me`);
    if (!data) {
      throw new Error('User profile data was not received.');
    }
    setStoredUser(data);
    return data;
  };

  const needsProfileCompletion = (user) => {
    if (!user) return true;
    return (
      !user.phone ||
      user.phone.trim() === '' ||
      !user.address ||
      !user.address.street1 ||
      !user.address.city ||
      !user.address.state ||
      !user.address.country
    );
  };

  const handleGitHubLogin = async () => {
    trackButtonClick('github_login', 'login_page');
    setLoading('github');
    setError('');
    try {
      const provider = new GithubAuthProvider();
      provider.addScope('read:user');
      provider.addScope('user:email');

      await signInWithPopup(auth, provider);
      const profile = await bootstrapAndFetchUser();

      trackFormSubmit('github_login', 'login_page', true);
      if (needsProfileCompletion(profile)) {
        navigate('/complete-profile', { state: { user: profile } });
        return;
      }

      navigate('/dashboard');
    } catch (err) {
      logger.error('GitHub login failed', err);
      trackFormSubmit('github_login', 'login_page', false, { error: err.message });
      if (err?.code === 'auth/popup-closed-by-user') {
        setError('GitHub sign-in was closed before completion. Please try again.');
      } else {
        setError('Failed to sign in with GitHub: ' + (err?.message || 'Please try again.'));
      }
      try { await auth.signOut(); } catch {}
    } finally {
      setLoading(null);
    }
  };

  const handleAzureDevOpsLogin = async () => {
    trackButtonClick('microsoft_login', 'login_page');
    setLoading('microsoft');
    setError('');
    try {
      const provider = new OAuthProvider('microsoft.com');
      const tenantId = process.env.REACT_APP_MS_TENANT_ID || process.env.REACT_APP_AZURE_TENANT_ID;
      if (tenantId) {
        provider.setCustomParameters({ tenant: tenantId });
      }
      provider.addScope('openid');
      provider.addScope('profile');
      provider.addScope('email');
      provider.addScope('User.Read');

      await signInWithPopup(auth, provider);
      const profile = await bootstrapAndFetchUser();

      trackFormSubmit('microsoft_login', 'login_page', true);
      if (needsProfileCompletion(profile)) {
        navigate('/complete-profile', { state: { user: profile } });
        return;
      }

      navigate('/dashboard');
    } catch (err) {
      logger.error('Microsoft login failed', err);
      trackFormSubmit('microsoft_login', 'login_page', false, { error: err.message });
      if (err?.code === 'auth/popup-closed-by-user') {
        setError('Microsoft sign-in was closed before completion. Please try again.');
      } else if (err?.code === 'auth/account-exists-with-different-credential') {
        setError('This email is already linked to another login method. Sign in with that method first, then link Microsoft from Profile Settings.');
      } else {
        setError('Failed to sign in with Microsoft: ' + (err?.message || 'Please try again.'));
      }
      try { await auth.signOut(); } catch {}
    } finally {
      setLoading(null);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    // Validate email field
    if (!email || email.trim() === '') {
      setError('Please enter your email address');
      return;
    }

    // Validate email format
    if (!EMAIL_REGEX.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate password field
    if (!password || password.trim() === '') {
      setError('Please enter your password');
      return;
    }

    setLoading('email');
    setError('');
    setNotice('');
    setResendInfo('');
    try {
      // Sign in with Firebase (Identity Platform)
      const credential = await signInWithEmailAndPassword(auth, email, password);

      // Enforce email verification before proceeding
      try {
        await credential.user.reload();
      } catch (_) {}
      if (!credential.user.emailVerified) {
        setError('Please verify your email before signing in. Check your inbox and spam/junk folders for the verification email.');
        await auth.signOut();
        setLoading(null);
        return;
      }

      // Bootstrap user (create if missing, idempotent) - fail fast if this fails
      try {
        await axios.post(`${API_BASE}/api/users/bootstrap`);
      } catch (bootstrapError) {
        logger.error('User bootstrap failed:', bootstrapError);
        setError('An error occurred while setting up your account. Please try logging in again.');
        await auth.signOut();
        setLoading(null);
        return;
      }
      // Fetch backend user profile via gateway (axios interceptor attaches the ID token) - fail fast if missing
      try {
        const { data } = await axios.get(`${API_BASE}/api/users/me`);
        if (data) {
          setStoredUser(data);
        } else {
          throw new Error('User profile data was not received.');
        }
      } catch (profileError) {
        logger.error('Failed to fetch user profile:', profileError);
        setError('Could not retrieve your user profile. Please try logging in again.');
        await auth.signOut();
        setLoading(null);
        return;
      }
      // Navigate to dashboard after successful login
      trackFormSubmit('email_login', 'login_page', true);
      navigate('/dashboard');
    } catch (err) {
      const errorMessage = 'Invalid email or password: ' + (err?.response?.data?.error || err.message);
      setError(errorMessage);
      localStorage.setItem('loginError', errorMessage);
      trackFormSubmit('email_login', 'login_page', false, { error: errorMessage });
    }
    setLoading(null);
  };

  const handleForgotPassword = async () => {
    // Clear any existing messages
    setError('');
    setForgotPasswordSuccess('');
    setResendInfo('');

    // Validate email is provided
    if (!email) {
      setError('Please enter your email address first');
      return;
    }

    // Validate email format
    if (!EMAIL_REGEX.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    trackButtonClick('forgot_password', 'login_page');
    setLoading('forgot');

    try {
      await sendPasswordResetEmail(auth, email, {
        url: `${window.location.origin}/auth/action`,
        handleCodeInApp: true,
      });
      setForgotPasswordSuccess('If an account exists for this email, a reset link was sent. Please check your inbox (and spam/junk) and follow the instructions to reset your password.');
    } catch (err) {
      logger.error('Forgot password error', err);
      const message = err?.code === 'auth/user-not-found'
        ? 'No account found with this email address. Please check your email or sign up for a new account.'
        : 'Failed to send password reset email. Please try again later.';
      setError(message);
    }

    setLoading(null);
  };

  const handleResendVerification = async () => {
    // Clear messages
    setError('');
    setForgotPasswordSuccess('');
    setResendInfo('');

    // Require email and password to authenticate before sending
    if (!email || !EMAIL_REGEX.test(email)) {
      setError('Please enter a valid email address first.');
      return;
    }
    if (!password) {
      setError('Please enter your password to resend verification.');
      return;
    }

    trackButtonClick('resend_verification', 'login_page');
    setLoading('resend');
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      try {
        await cred.user.reload();
      } catch (_) {}

      if (cred.user.emailVerified) {
        setResendInfo('Your email is already verified. You can sign in now.');
        await auth.signOut();
        setLoading(null);
        return;
      }

      await sendEmailVerification(cred.user, {
        url: `${window.location.origin}/auth/action`,
        handleCodeInApp: true,
      });
      setResendInfo('Verification email resent. Please check your inbox (and spam/junk) to verify before signing in.');
    } catch (err) {
      logger.error('Resend verification error', err);
      const msg = err?.code === 'auth/user-not-found'
        ? 'No account found with this email address.'
        : err?.code === 'auth/wrong-password'
          ? 'Incorrect password. Please try again.'
          : 'Could not resend verification email. Please try again.';
      setError(msg);
    } finally {
      try { await auth.signOut(); } catch {}
      setLoading(null);
    }
  };



  return (
    <>
      <SEO
        title="Login - Sniffr.ai | AI-Powered Code Review Platform"
        description="Sign in to Sniffr.ai to access AI-powered code review, automated pull request analysis, and code quality insights. Login with GitHub, GitLab, or email."
        keywords="Sniffr.ai login, code review login, AI code review sign in, GitHub login, GitLab login, developer tools login"
        url="/login"
      />
      <Box sx={{
      minHeight: "100vh", display: "flex", alignItems: "center",
      justifyContent: "center",
      background: '#fff'
    }}>
      <Card sx={{
        width: '100%',
        maxWidth: 1100,
        height: 900,
        display: 'flex',
        borderRadius: 2,
        boxShadow: '0 20px 40px rgba(40, 34, 34, 0.1)',
        overflow: 'hidden',
        padding: '30px',
        border: '1px solid #e0e0e0'
      }}>
        {/* Left Panel - Gradient Background */}
        <Box
          sx={{
            flex: 1,
            background: 'linear-gradient(135deg,hsl(16 100% 66%),hsl(195 100% 50%),hsl(270 100% 70%))',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            borderRadius: 2,
            p: 4,
            position: 'relative',
          }}
        >
          {/* <Box sx={{ position: 'relative', zIndex: 2, maxWidth: 400 }}> */}
          {/* Logo */}
          <Box
            onClick={() => navigate('/')}
            sx={{ display: 'flex', alignItems: 'center', mb: 4, cursor: 'pointer', width: 'fit-content' }}
          >
            <Box
              component="img"
              sx={{ height: 40, mr: 2, borderRadius: 5, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}
              alt="Logo"
              src="/assets/logo.png"
            />
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, '&:hover': { opacity: 0.85 }, transition: 'opacity 0.2s' }}>
              Sniffr.ai
            </Typography>
          </Box>

          {/* Welcome Text */}
          <Box>
            <Typography
              variant="h3"
              sx={{
                color: '#fff',
                fontWeight: 800,
                fontSize: 30,
                mb: 2,
                lineHeight: 2
              }}
            >
              Welcome to Sniffr.ai
            </Typography>

            {/* Feature List */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: 'hsl(16 100% 66%)',
                    mr: 2,
                    opacity: 0.8,
                  }}
                />
                <ArchitectureIcon sx={{ fontSize: 20, color: '#fff', mr: 1 }} />
                <Typography sx={{ color: '#fff', fontSize: 16, fontWeight: 500 }}>
                  Think like an architect
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: 'hsl(16 100% 66%)',
                    mr: 2,
                    opacity: 0.8,
                  }}
                />
                <AssignmentTurnedInIcon sx={{ fontSize: 20, color: '#fff', mr: 1 }} />
                <Typography sx={{ color: '#fff', fontSize: 16, fontWeight: 500 }}>
                  Validate like a BA
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: 'hsl(16 100% 66%)',
                    mr: 2,
                    opacity: 0.8,
                  }}
                />
                <ScienceIcon sx={{ fontSize: 20, color: '#fff', mr: 1 }} />
                <Typography sx={{ color: '#fff', fontSize: 16, fontWeight: 500 }}>
                  Test like senior QA
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'left' }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderRadius: 2
            }}>
              <Box sx={{ width: 30, height: 6, bgcolor: '#fff', borderRadius: 2 }} />
              <Box sx={{ width: 8, height: 6, borderRadius: '50%', bgcolor: '#fff' }} />
            </Box>
          </Box>
        </Box>

        {/* Right Panel - Login Form */}
        <Box
          sx={{
            flex: 1,
            bgcolor: '#fff',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            p: { xs: 2, sm: 4 },
            maxWidth: { xs: '100%', md: '50%' },
            minHeight: { xs: '100vh', md: 'auto' }
          }}
        >
          <Box sx={{ width: '100%', height: '100%', maxWidth: 500, display: 'flex', flexDirection: 'column',gap: 6 }}>
            {/* Top Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box
                onClick={() => navigate('/')}
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              >
                <Box
                  component="img"
                  sx={{ height: 24, mr: 1, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}
                  alt="Logo"
                  src="/assets/logo.png"
                />
                <Typography variant="h6" sx={{ color: '#000', fontWeight: 600, fontSize: 18, '&:hover': { color: '#374151' }, transition: 'color 0.2s' }}>
                  Sniffr.ai
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.1, backgroundColor: 'hsl(0 0% 96%)', padding: '0.25rem', borderRadius: 2 }}>
                <Button
                  variant="text"
                  sx={{
                    color: '#000',
                    bgcolor: '#fff',
                    textTransform: 'none',
                    fontSize: 14,
                    fontWeight: 500,
                    borderRadius: 2,
                    px: 3,
                  }}
                >
                  Sign In
                </Button>
                <RouterLink to="/signup" style={{ textDecoration: 'none' }}>
                  <Button 
                    onClick={() => trackButtonClick('sign_up_header', 'login_page')}
                    sx={{
                      color: '#666',
                      textTransform: 'none',
                      fontSize: 14,
                      fontWeight: 500,
                      px: 3,
                    }} >
                    Sign Up
                  </Button>
                </RouterLink>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 10 }}>

            
            {/* Login Form */}
            <Box component="form" onSubmit={handleEmailLogin} sx={{ mt: 4}}>
              <TextField
                required
                fullWidth
                id="email"
                label="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                type="email"
                disabled={!!loading}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#e0e0e0' },
                    '&:hover fieldset': { borderColor: '#bdbdbd' },
                    '&.Mui-focused fieldset': { borderColor: '#2196f3' },
                    borderRadius: 1,
                    bgcolor: '#fff',
                    fontSize: 14,
                    height: '50px'
                  },
                  '& .MuiOutlinedInput-input': {
                    py: 1.5,
                    fontSize: 14,
                    height: '50px'
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutline sx={{ color: '#9e9e9e' }} />
                    </InputAdornment>
                  ),
                  endAdornment: email && (
                    <InputAdornment position="end">
                      {isEmailValid ? (
                        <CheckCircle sx={{ color: 'success.main' }} />
                      ) : null}
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                required
                fullWidth
                id="password"
                label="Password"
                value={password}
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
                type={showPassword ? 'text' : 'password'}
                disabled={!!loading}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#e0e0e0' },
                    '&:hover fieldset': { borderColor: '#bdbdbd' },
                    '&.Mui-focused fieldset': { borderColor: '#2196f3' },
                    borderRadius: 1,
                    bgcolor: '#fff',
                    fontSize: 14,
                    height: '50px'
                  },
                  '& .MuiOutlinedInput-input': {
                    py: 1.5,
                    fontSize: 14,
                    height: '50px'
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: '#9e9e9e' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <Box sx={{ textAlign: 'right', mb: 3 }}>
                <Button
                  variant="text"
                  onClick={handleForgotPassword}
                  disabled={!!loading}
                  sx={{
                    color: '#666',
                    fontSize: 14,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                    '&:disabled': { color: '#ccc' }
                  }}>
                  {loading === 'forgot' ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                  Forgot your password
                </Button>
                <Button
                  variant="text"
                  onClick={handleResendVerification}
                  disabled={!!loading}
                  sx={{
                    color: '#666',
                    fontSize: 14,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                    '&:disabled': { color: '#ccc' },
                    ml: 1
                  }}>
                  {loading === 'resend' ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                  Resend verification
                </Button>
              </Box>

              {notice && (
                <Alert severity="info" sx={{ mb: 3, fontSize: 14 }}>
                  {notice}
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 3, fontSize: 14 }}>
                  {error}
                </Alert>
              )}

              {forgotPasswordSuccess && (
                <Alert severity="success" sx={{ mb: 3, fontSize: 14 }}>
                  {forgotPasswordSuccess}
                </Alert>
              )}
              {resendInfo && (
                <Alert severity="info" sx={{ mb: 3, fontSize: 14 }}>
                  {resendInfo}
                </Alert>
              )}

              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={!!loading}
                sx={{
                  bgcolor: '#000',
                  color: '#fff',
                  py: 1.5,
                  borderRadius: 1,
                  textTransform: 'none',
                  fontSize: 16,
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#333' },
                  '&:disabled': { bgcolor: '#ccc' }
                }}
              >
                {loading === 'email' ? <CircularProgress size={24} color="inherit" /> : 'Login'}
              </Button>
            </Box>

            <Divider sx={{ my: 1, color: '#999' }}>OR</Divider>

            {/* bottom Navigation */}
            <Box>
              {/* OAuth Buttons */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={loading === 'github' ? <CircularProgress size={16} /> : <GitHubIcon />}
                  onClick={handleGitHubLogin}
                  disabled={!!loading}
                  sx={{
                    flex: 1,
                    py: 1.5,
                    borderColor: '#e0e0e0',
                    color: '#333',
                    textTransform: 'none',
                    fontSize: 16,
                    fontWeight: 500,
                    borderRadius: 1,
                    '&:hover': { borderColor: '#bdbdbd', bgcolor: '#fafafa' }
                  }}
                >
                  GitHub
                </Button>
                <Button
                  variant="outlined"
                  startIcon={loading === 'microsoft' ? <CircularProgress size={16} /> : <FaMicrosoft />}
                  onClick={handleAzureDevOpsLogin}
                  disabled={!!loading}
                  sx={{
                    flex: 1,
                    py: 1.5,
                    borderColor: '#e0e0e0',
                    color: '#333',
                    textTransform: 'none',
                    fontSize: 16,
                    fontWeight: 500,
                    borderRadius: 1,
                    '&:hover': { borderColor: '#bdbdbd', bgcolor: '#fafafa' }
                  }}
                >
                  Microsoft
                </Button>
              </Box>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: '#666', fontSize: 14 }}>
                  Don't have an account?{' '}
                  <Link
                    onClick={() => {
                      trackButtonClick('sign_up_bottom', 'login_page');
                      navigate('/signup');
                    }}
                    sx={{
                      color: '#000',
                      fontWeight: 600,
                      cursor: 'pointer',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    Sign Up
                  </Link>
                </Typography>
              </Box>
            </Box>

            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
    </>
  );
}

export default Login;